import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { CountrySelect } from "@/Common/Components/Contacts/CountrySelect";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { SiteCreateState } from "../SiteCreate";

interface IPhoneSectionProps {
    phones: PhoneNumber[];
    phonesToRemove: string[];
    mutateState: (mutation: (state: SiteCreateState) => void, then?: () => void) => void;
}

function PhoneSection(props: IPhoneSectionProps): JSX.Element {
    const {
        phones,
        phonesToRemove,
        mutateState
    } = props;

    function onAddPhone(): void {
        mutateState(s => {
            s.phones = phones;
            s.phones.push(PhoneNumber.newPhone(ContactPointType.Work, PhoneType.Phone));
        });
    }

    function onPhoneRemove(idx: number): void {
        mutateState(s => {
            s.phones = phones;
            s.phonesToRemove = phonesToRemove;

            let phoneToRemove = s.phones.splice(idx, 1);
            s.phonesToRemove.push(phoneToRemove[0].ContactPoint_id);

            if (s.phones.length === 0) {
                s.phones.push(PhoneNumber.newPhone(ContactPointType.Work, PhoneType.Phone));
            }
        });
    }

    function onPhoneTypeChange(idx: number, event: React.ChangeEvent<HTMLSelectElement>): void {
        let typeValue = event.target.value;

        let contactPointLookupId = typeValue.split("_")[0];
        let phoneLookupId = typeValue.split("_")[1];

        let refworkLookupId = cacheStore.getLookupByContactPointType(ContactPointType.Work).LookupID;
        let refphoneLookupId = cacheStore.getLookupByPhoneType(PhoneType.Phone).LookupID;

        mutateState(s => {
            s.phones = phones;
            let phone = phones[idx];

            phone.PhoneTypeLookupId = phoneLookupId;
            phone.ContactPointTypeLookupId = contactPointLookupId;

            let hasExtensionField = (phone.ContactPointTypeLookupId == refworkLookupId) && (refphoneLookupId == phone.PhoneTypeLookupId);

            if (!hasExtensionField)
                phone.Extension = null;

            s.phones[idx] = phone;
        });
    }

    function onPhoneNumberChange(idx: number, phoneNumber: string): void {
        mutateState(s => {
            s.phones = phones;
            let phone = phones[idx];

            phone.Number = phoneNumber;
            phone.IsValid = PhoneNumber.validatePhone(phone);

            s.phones[idx] = phone;
        });
    }

    function onPhoneExtensionChange(idx: number, extension: string): void {
        mutateState(s => {
            s.phones = phones;
            let phone = phones[idx];

            phone.Extension = extension;
            phone.IsValid = PhoneNumber.validatePhone(phone);

            s.phones[idx] = phone;
        });
    }

    function onPhoneIsPreferredChange(idx: number, isPreferred: boolean): void {
        mutateState(s => {
            s.phones = phones;
            let phone = s.phones[idx];

            phone.IsPreferred = isPreferred;

            s.phones.forEach(p => {
                if (p.renderId !== phone.renderId) {
                    p.IsPreferred = false;
                }
            });
        });
    }

    return (
        <div>
            <div className="view-section-header">
                <h3 className="view-section-title">{cacheStore.GetString("PhoneLabel")}</h3>
                <div className="text-right push-right-25">
                    <a onClick={() => onAddPhone()}>{cacheStore.GetString("AddLabel")}</a>
                </div>
            </div>
            {phones.map((x: PhoneNumber, idx: number) => (
                <div className="view-section-body" key={"__p" + x.renderId}>
                    <div className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                            <label className="form-label">{cacheStore.GetString("TypeLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-9 col-sm-12 ">
                            <select onChange={e => onPhoneTypeChange(idx, e)} className="form-control myle-control" value={x.ContactPointTypeLookupId + "_" + x.PhoneTypeLookupId}>
                                {x.AvailablePhoneTypeLookupIds.map((z: [FlatLookupView, FlatLookupView]) => {
                                    let ct: FlatLookupView = z[0];
                                    let pt = z[1];
                                    return <option key={ct.LookupID + "_" + pt.LookupID} value={ct.LookupID + "_" + pt.LookupID}> {cacheStore.GetLocalizedValueByLookupId(ct.LookupID) + " - " + cacheStore.GetLocalizedValueByLookupId(pt.LookupID)}</option>;
                                })}
                            </select>
                        </div>

                        <span onClick={() => onPhoneRemove(idx)} style={{ color: "red", fontWeight: "bold", cursor: "pointer" }}>X</span>
                    </div>
                    <div className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                            <label className="form-label">{cacheStore.GetString("CountryLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-9 col-sm-12 ">
                            <CountrySelect
                                dataCy="phone-section-country-select"
                                onCountryChange={value => mutateState(state => state.phones[idx].CountryLookupId = value)}
                                initialCountryLookupId={x.CountryLookupId}
                            />
                        </div>
                    </div>
                    <div className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                            <label className="form-label">{cacheStore.GetString("NumberLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-9 col-sm-12">
                            <TextboxMaskedControl
                                isValid={x.IsValid}
                                allowEmpty={false}
                                id={"phone" + x.renderId}
                                value={x.Number}
                                onChangeFunction={e => onPhoneNumberChange(idx, e)}
                                item={x}
                                mask={PhoneNumber.getMask(x)}
                                validationErrorMessage={PhoneNumber.getValidationErrorMessage(x)}
                            />
                        </div>
                    </div>
                    {PhoneNumber.hasExtensionField(x.PhoneTypeLookupId) && (
                        <div className="row" style={{ marginBottom: "15px" }}>
                            <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("ExtLabel")}</label>
                            </div>
                            <div className="col-lg-5 col-md-9 col-sm-12">
                                <TextboxControl
                                    IsNumeric={true}
                                    id={"phoneExt" + x.ContactPoint_id}
                                    value={x.Extension ?? ""}
                                    onChange={e => onPhoneExtensionChange(idx, e)}
                                    maxLength={7}
                                />
                            </div>
                        </div>
                    )}
                    <div className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right" />
                        <div className="col-lg-5 col-md-9 col-sm-12">
                            <CheckboxControl
                                checked={x.IsPreferred}
                                label={cacheStore.GetString("PreferredPhoneDLabel")}
                                onChange={e => onPhoneIsPreferredChange(idx, e)}
                                item={x}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default PhoneSection;
